/**
 * Which tool a room runs when it opens, and how its answers are counted.
 *
 * The live board creates the tool session itself when it is opened with
 * `?startRanking=1`. Before this, it read the tool only from the Event
 * Designer's step config and fell back to stack ranking, so a session created
 * with a method of its own (the Session app, "Run it live") ran the wrong tool;
 * and it hard-coded the output type to `ranking`, so a scoring or sorting method
 * was counted as a ranking. The method the session declares comes first, and
 * the output type follows the method (the one counting rule, #1009).
 */
import { normalizeSessionToolId, resolveSessionToolDescriptor } from '@/lib/sessionToolCatalog';
import { getOutputTypeForSessionTool } from '@/lib/toolRegistry';
import type { ToolOutputType } from '@/types/prioritization/toolSession';

export interface LaunchTool {
  toolId: string;
  toolName: string;
  outputType: ToolOutputType;
}

function stepToolFrom(v3Config: unknown, step: string | null | undefined): string | undefined {
  if (!v3Config || typeof v3Config !== 'object') return undefined;
  const steps = (v3Config as { steps?: unknown }).steps;
  if (!steps || typeof steps !== 'object') return undefined;
  const read = (key: string): string | undefined => {
    const cfg = (steps as Record<string, unknown>)[key];
    const toolId = cfg && typeof cfg === 'object' ? (cfg as { toolId?: unknown }).toolId : undefined;
    return typeof toolId === 'string' && toolId ? toolId : undefined;
  };
  return (step ? read(step) : undefined) ?? read('S5');
}

export function resolveLaunchTool(input: {
  /** The method declared on the session row, when there is one. */
  selectedToolId?: string | null;
  /** The Event Designer's step config (`modeConfig.v3Config`), when the session is a designed event. */
  v3Config?: unknown;
  /** The step being launched; S5 (ranking) when not a designed event. */
  step?: string | null;
}): LaunchTool {
  const stepTool = stepToolFrom(input.v3Config, input.step);
  const declared = normalizeSessionToolId(input.selectedToolId ?? undefined);
  const toolId = normalizeSessionToolId(stepTool) ?? declared ?? 'stack_ranking';
  const descriptor = resolveSessionToolDescriptor(toolId);
  return {
    toolId,
    toolName: descriptor?.name ?? toolId,
    outputType: getOutputTypeForSessionTool(toolId),
  };
}
